//      
const jsforce = require('jsforce');
const urlParse = require('url-parse');
/**
 * Represents a wrapper for communicating with the Salesforce REST API via the JSForce library.
 * @param {string} URL - The pod URL to which we are connecting.
 * @param {string} sessionId A valid session id.
 * @param {string} proxyUrl The proxy url for the connection
 */
class Client {
  constructor(URL         , sessionId         , proxyUrl         ) {
    const options = {
      instanceUrl: urlParse(URL).origin,
      accessToken: sessionId
    };

    if (proxyUrl) {
      options.proxyUrl = proxyUrl;
    }

    this.sfClient = new jsforce.Connection(options);
  }
  _constructSOQL(objectName        , fields               , where         , sort         , limit         ) {
    const soql = ['SELECT', fields.join(), 'FROM', objectName];
    if (where) {
      soql.push('WHERE', where);
    }
    if (sort && sort.length) {
      soql.push('ORDER BY', sort);
    }
    if (limit) {
      soql.push('LIMIT', limit);
    }
    return soql.join(' ');
  }
  _idWhere(id        ) {
    return `ID = '${id}'`;
  }
  _transformReturn(objectName        , resp        ) {
    return { success: true, record_count: resp.totalSize, [objectName]: resp.records };
  }
  /**
   *  Run a query
   *  @private
   *  @param {object} config - {object: String, fields: Array, where: String, sort: String, limit: String}
   *  @param {string} config.object - Name of the object to query
   *  @param {array} config.fields - An array of field names to return
   *  @param {string} config.where - the where statement without the "where" string. ex: where: "Name = 'Bob\'s Port-o-potties"
   *  @param {string} config.sort - the sort statment without the "order by" string. ex: sort: "LastModifiedDate DESC"
   *  @param {integer} config.limit - the number of records to return.
   *  @return {promise} - The promise returned when resolved will return the results of the query.
   * */
  queryObject(config        )                  {
    const soql = this._constructSOQL(config.object, config.fields, config.where, config.sort, config.limit);
    return this.sfClient.query(soql).then(
      args => {
        // console.log('success', args);
        return args;
      },
      args => {
        console.log('failure', args);
        return Promise.reject(args);
      }
    );
  }
  /**
   *  Run a query
   *  @param {object} config - {object: String, fields: Array, where: String, sort: String, limit: String}
   *  @param {string} config.object - Name of the object to query
   *  @param {array} config.fields - An array of field names to return
   *  @param {string} config.where - the where statement without the "where" string. ex: where: "Name = 'Bob\'s Port-o-potties"
   *  @param {string} config.sort - the sort statment without the "order by" string. ex: sort: "LastModifiedDate DESC"
   *  @param {integer} config.limit - the number of records to return.
   *  @return {promise} - The promise returned when resolved will return the results of the query.
   * */
  runQuery(config        )                  {
    return this.queryObject(config).then(results => {
      return this._transformReturn(config.object, results);
    });
  }
  /**
   *  Run a query to get the labels of object(s)
   *  @param {array} objectNames - An array of object names for which to get labels.
   *  @return {promise} - The promise returned when resolved will return the results of the query.
   * */
  getObjectLabels(objectNames               )                  {
    const promisePool = [];
    let object;
    while ((object = objectNames.pop())) {
      promisePool.push(this.sfClient.describe(object));
    }
    return Promise.all(promisePool).then(function processResponses(responses) {
      // coallesce all the object labels into a single return value
      let response;
      const labels = { success: true };
      // {success: true, Medical_Event_vod__c: [{plural: "Medical Events", singular: "Medical Event"}]}
      while ((response = responses.pop())) {
        const describe = response.objectDescribe;
        labels[response.name] = { singular: response.label, plural: response.labelPlural };
        // put the labels into a labels object or array
      }
      return labels;
    });
  }
  /**
   *  Run a query to get the a certain field value for the object that is the current top-level subject.
   *  @param {string} objectName - Name of the object.
   *  @param {string} field - Name of the field
   *  @param {string} id - ID of the object
   *  @return {promise} - The promise returned when resolved will return the results of the query.
   * */
  getFieldValue(objectName        , field        , id        )                  {
    return this.queryObject({ objectName, fields: [field], where: this._idWhere(id) }).then(results => {
      const resp = this._transformReturn(objectName, results);
      const record = results.records[0];
      resp[objectName] = { [field]: record && record[field] ? record[field] : '' };
      return resp;
    });
  }
  /**
   *  Run a query to get the labels of fields
   *  @param {object} config - {object: String, fields: Array}
   *  @param {string} config.object - Name of the object for which to get the field labels
   *  @param {array} config.fields - An array of field names for which to get the field labels
   *  @return {promise} - The promise returned when resolved will return the results of the query.
   * */
  getFieldLabels(config        )                  {
    return this.sfClient.describe(config.object).then(
      function processDescribe(describe) {
        const resp = { success: true };
        const { fields } = describe;
        const newFields = {};
        let field;
        while ((field = fields.pop())) {
          newFields[field.name] = field.label;
        }
        resp[config.object] = newFields;
        return resp;
      },
      err => {
        console.log(err);
      }
    );
  }
  /**
   *  Run a query to get the value labels of the items in a picklist
   *  @param {object} config - {object: String, field: String}
   *  @param {string} config.object - Name of the object for which to get the picklist value labels
   *  @param {string} config.field - The picklist field name
   *  @return {promise} - The promise returned when resolved will return the results of the query which will be an array of the value labels.
   * */
  getPicklistValueLabels(config        )                  {
    const describePromise = this.sfClient.describe(config.object);
    const getPicklistValueLabelsFromResp = function processDescribe(describe) {
      const resp = { success: true };
      const { fields } = describe;
      const picklistValueLabels = {};
      const fieldLabel = {};
      fieldLabel[config.field] = picklistValueLabels;
      let field;
      let name;
      let picklist;
      resp[config.object] = fieldLabel;
      while ((field = fields.pop())) {
        name = field.name;
        if (name === config.field) {
          // at this point we found the field that is the picklist we are looking for. the picklist values are an array and we need to convert them to an object.
          while ((picklist = field.picklistValues.pop())) {
            picklistValueLabels[picklist.value] = picklist.label;
          }
          break;
        }
      }
      return resp;
    };
    return describePromise.then(getPicklistValueLabelsFromResp);
  }
  /**
   *  Run a query to get the text value of the requested veeva message
   *  @param {string} languageLocaleKey - the language value from the user's profile
   *  @param {array} msgsToGet - array of string names of the messages desired.
   *  @return {promise} - The promise returned when resolved will return the results of the messages query which will be an array of the value labels.
   * */
  getVeevaMessages(languageLocaleKey        , messageNames               )                  {
    const config = {
      object: 'Message_vod__c',
      fields: ['Name', 'Category_vod__c', 'Language_vod__c', 'Text_vod__c'],
      where: ''
    };
    const whereSubClauses = [];
    const msgsToGet = messageNames.slice();
    let message                                    ;
    while ((message = msgsToGet.pop())) {
      whereSubClauses.push(`(Name='${message.name}' AND Category_vod__c='${message.category}' AND Active_vod__c=true)`);
    }
    config.where = `(${whereSubClauses.join(' OR ')}) AND Language_vod__c='${languageLocaleKey}'`;
    return this.runQuery(config);
  }
  searchObject(config) {
    const sosl = this._constructSOSL(config.searchQuery, config.searchGroup, config.objectsAndFields, config.limit);
    return this.sfClient.search(sosl).then(
      args => {
        return args;
      },
      args => {
        console.log('failure', args);
        return Promise.reject(args);
      }
    );
  }

  _constructSOSL(searchQuery, searchGroup, objectsAndFields, limit) {
    const soql = ['FIND', searchQuery];
    if (searchGroup) {
      soql.push('IN', searchGroup);
    }
    if (objectsAndFields) {
      soql.push('RETURNING', objectsAndFields);
    }
    if (limit) {
      soql.push('LIMIT', limit);
    }
    return soql.join(' ');
  }

  escapeStringForSOSLAllowWildcards(userInput) {
    return userInput
      .replace(/\\/g, '\\\\')
      .replace(/\?/g, '\\?')
      .replace(/&/g, '\\&')
      .replace(/\|/g, '\\|')
      .replace(/!/g, '\\!')
      .replace(/{/g, '\\{')
      .replace(/}/g, '\\}')
      .replace(/\[/g, '\\[')
      .replace(/]/g, '\\]')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)')
      .replace(/\^/g, '\\^')
      .replace(/~/g, '\\~')
      .replace(/:/g, '\\:')
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\+/g, '\\+')
      .replace(/-/g, '\\-');
  }
}
module.exports = Client;
